import { useState } from "react";
import { Camera } from "@/data/cameras";
import { Badge } from "@/components/ui/badge";
import { Wifi, Zap, Mic, Eye, Layers, Shield, Sun, ExternalLink, Camera as CameraIcon, Users, Car } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

interface CameraCardProps {
  camera: Camera;
  highlight?: boolean;
}

export function CameraCard({ camera, highlight = false }: CameraCardProps) {
  const { t } = useLang();
  const lensDisplay = camera.lensMotorized
    ? `${camera.lensMotorized} (Mtz)`
    : camera.lensFixed || "—";
  const [imgError, setImgError] = useState(false);

  return (
    <div
      className={`bg-card border rounded-lg overflow-hidden flex flex-col transition-all hover:shadow-md hover:border-primary/40 ${
        highlight ? "border-primary ring-1 ring-primary/30 shadow-sm" : "border-border"
      }`}
    >
      {/* Product Image */}
      <div className="relative h-36 bg-muted/30 flex items-center justify-center overflow-hidden border-b border-border">
        {camera.image && !imgError ? (
          <img
            src={camera.image}
            alt={camera.model}
            className="h-full w-full object-contain p-2"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-muted-foreground/40">
            <CameraIcon className="w-10 h-10" />
            <span className="text-xs">{camera.type}</span>
          </div>
        )}
        {/* Brand badge overlay */}
        <span className={`absolute top-2 left-2 text-xs font-bold px-2 py-0.5 rounded-full shadow-sm ${
          camera.brand === "DAHUA"
            ? "bg-blue-600 text-white"
            : "bg-orange-500 text-white"
        }`}>
          {camera.brand}
        </span>
        {camera.price && (
          <span className="absolute top-2 right-2 text-xs font-semibold bg-card/90 border border-border text-foreground px-2 py-0.5 rounded-full shadow-sm">
            {camera.price}
          </span>
        )}
      </div>

      <div className="p-4 flex flex-col gap-3 flex-1">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                camera.brand === "DAHUA"
                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                  : "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
              }`}
            >
              {camera.brand}
            </span>
            <span className="text-xs text-muted-foreground">{camera.type}</span>
            {camera.color && (
              <span className="text-xs text-muted-foreground">· {camera.color}</span>
            )}
            {camera.ref && (
              <span className="text-xs text-muted-foreground font-mono">#{camera.ref}</span>
            )}
          </div>
          <p className="text-sm font-mono font-semibold text-foreground leading-tight break-all" title={camera.model}>
            {camera.model}
          </p>
        </div>
        <div className="text-right shrink-0">
          <span className="text-2xl font-bold text-primary">{camera.megapixels}</span>
          <span className="text-xs text-muted-foreground ml-0.5">MP</span>
        </div>
      </div>

      {/* Specs grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground border-t border-border pt-3">
        <div className="flex items-center gap-1">
          <Eye className="w-3 h-3 shrink-0" />
          <span>IR: <strong className="text-foreground">{camera.irRange > 0 ? `${camera.irRange}m` : "—"}</strong></span>
        </div>
        <div className="flex items-center gap-1">
          <Layers className="w-3 h-3 shrink-0" />
          <span className="truncate">Lente: <strong className="text-foreground">{lensDisplay}</strong></span>
        </div>
      </div>

      {/* Feature badges */}
      <div className="flex flex-wrap gap-1">
        {camera.hasWifi && (
          <Badge variant="secondary" className="text-xs gap-1 py-0">
            <Wifi className="w-2.5 h-2.5" /> WiFi
          </Badge>
        )}
        {camera.has4G && (
          <Badge variant="secondary" className="text-xs gap-1 py-0">
            <Zap className="w-2.5 h-2.5" /> 4G
          </Badge>
        )}
        {camera.hasSolar && (
          <Badge variant="secondary" className="text-xs gap-1 py-0">
            <Sun className="w-2.5 h-2.5" /> Solar
          </Badge>
        )}
        {camera.hasAudio && (
          <Badge variant="secondary" className="text-xs gap-1 py-0">
            <Mic className="w-2.5 h-2.5" /> {t("audio")}
          </Badge>
        )}
        {camera.hasDualLight && (
          <Badge className="text-xs gap-1 py-0 bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-700">
            Dual
          </Badge>
        )}
        {camera.hasColorNight && (
          <Badge className="text-xs gap-1 py-0 bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 border-violet-200 dark:border-violet-700">
            <Sun className="w-2.5 h-2.5" /> Full Color
          </Badge>
        )}
        {camera.wdrType && (
          <Badge className={`text-xs py-0 ${camera.wdrType === 'WDR' ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300 border-sky-200 dark:border-sky-700' : 'bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-300 border-slate-200 dark:border-slate-600'}`}>
            {camera.wdrType}
          </Badge>
        )}
        {camera.detPeople && !camera.detVehicle && (
          <Badge className="text-xs gap-1 py-0 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700">
            <Users className="w-2.5 h-2.5" /> {t("detPeople")}
          </Badge>
        )}
        {camera.detVehicle && (
          <Badge className="text-xs gap-1 py-0 bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 border-purple-200 dark:border-purple-700">
            <Car className="w-2.5 h-2.5" /> {t("detPeopleVehicle")}
          </Badge>
        )}
        {camera.hasIK10 && (
          <Badge variant="outline" className="text-xs gap-1 py-0">
            <Shield className="w-2.5 h-2.5" /> IK10
          </Badge>
        )}
        {camera.hasSDCard && (
          <Badge variant="outline" className="text-xs py-0">SDCard</Badge>
        )}
      </div>

      {/* Link to product */}
      {camera.link && (
        <a
          href={camera.link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary hover:underline flex items-center gap 1 border-t border-border pt-2"
        >
          <ExternalLink className="w-3 h-3" />
          {t("viewProduct")}
        </a>
      )}
      </div>
    </div>
  );
}
